import * as Immutable from 'immutable';
import * as React from 'react';
import IPatternCategoryStore from '../../_stores/PatternCategoryStore';
import IPatternStore from '../../_stores/PatternStore';
import PatternBrowserCategoryContainer from '../_containers/PatternBrowserCategoryContainer';
import PatternBrowserItem from './PatternBrowserItem';
import RateLimiter from '../../../Common/Utils/RateLimiter';
import { Trans } from 'react-i18next';
import { PatternsPerPage } from '../../PatternDesignServiceSettings';
import { Grid, Skeleton } from '@mui/material';

export type Props = {
	onClose: Function
	onSelect: (id:string, categoryId:string) => void
	opened: boolean
	search: string
	selectedPattern: string
	selectedPatternCategory: string
	categories?: Immutable.OrderedMap<string, IPatternCategoryStore>
	getCategories?: Function
	getPatterns?: Function
	offset?: number
	loading?: boolean
	onSelectCategory?: Function
	patterns?: Immutable.OrderedMap<string, IPatternStore>
	resetPatterns?: Function
}

type State = {
	selectedTopLevelCategoryId?: number
	selectedCategoryId?: number
}

const T = ({children, ...otherProps}:any) => <Trans ns="create" {...otherProps}>{children}</Trans>

export default class PatternBrowserCategoryList extends React.Component<Props, State> {
	private searchRL:RateLimiter;
	private ref:React.RefObject<HTMLDivElement>;

	state:State = { 
		selectedTopLevelCategoryId: 0,
		selectedCategoryId: 0
	};

	constructor(props:Props) {
		super(props);
		this.ref = React.createRef();
	}

	public componentDidMount() {
		this.searchRL = new RateLimiter(500);
		
		if(!this.props.categories.count()) this.props.getCategories();
	}

	public componentWillUnmount() {
		this.searchRL.stop();
	}

	public componentDidUpdate(prevProps:Props, prevState:State) {
		// Fetch new patterns if we just opened and need to change category
		if(this.props.opened && !prevProps.opened) {
			this.onSelectCategory(+this.props.selectedPatternCategory)
		}

		//Fetch new patterns if we have a category and search chagned
		else if(
			(this.props.search != prevProps.search || this.props.offset != prevProps.offset)
			&& this.state.selectedCategoryId
		) {
			//this.props.resetPatterns();
			this.searchRL.throttle(() => {
				this.props.getPatterns(this.state.selectedCategoryId, this.props.search, PatternsPerPage, this.props.offset);
			});
		}
	}

	private getTopLevel = (id:string) => {
		return this.props.categories.find((topValue, topKey) => {
			return topValue.get('categories').has(+id);
		}).get('id');
	}

	private isVisible = (name:string) => name.toLowerCase().includes(this.props.search.toLowerCase())

	private onSelectCategory(id:number, selectedTopLevelCategoryId?:number) {
		let defaultTopLevelCategoryId = id > 0 ? this.getTopLevel(String(id)) : 0;
		let topLevelCategoryId =  (selectedTopLevelCategoryId !== undefined) ? selectedTopLevelCategoryId : defaultTopLevelCategoryId;
		this.setState({
			selectedTopLevelCategoryId: topLevelCategoryId,
			selectedCategoryId: id
		});
		this.props.onSelectCategory(id);
		//this.props.resetPatterns();

		if(id > 0) {
			this.props.getPatterns(id, this.props.search, PatternsPerPage, this.props.offset);
		}
	}

	private onSelectTopLevelCategory(id:number) {
		this.setState({
			selectedTopLevelCategoryId: id
		});
	}

	private getCategories() {
		let categories = this.props.categories;

		//Show subcategories of selected category if we have one
		if(this.state.selectedTopLevelCategoryId > 0) {
			categories = categories.getIn([this.state.selectedTopLevelCategoryId,'categories']);
		}

		return categories.entrySeq().map(([id, category]) => {
			return <Grid item xs={4} sm={4} lg={3} key={id}>
				<PatternBrowserCategoryContainer category={category} onClick={(this.state.selectedTopLevelCategoryId > 0 ? this.onSelectCategory.bind(this) : this.onSelectTopLevelCategory.bind(this))}/>
			</Grid>
		});
	}

	private renderPatterns(patterns:Immutable.OrderedMap<string, IPatternStore>) {
		return patterns.sort((a:IPatternStore, b:IPatternStore) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0).entrySeq().map(([id, pattern]) => {
			return <Grid item xs={4} sm={4} lg={3} key={id}>
				<PatternBrowserItem selected={this.props.selectedPattern == id} id={id} src={pattern.get('src')} name={pattern.get('name')} onSelect={() => this.props.onSelect(id, String(this.state.selectedCategoryId))} onClose={this.props.onClose}/>
			</Grid>
		});
	}

	private renderSkeletons(count:number) {
		const height = this.state.selectedCategoryId ? 250 : 110;
		let Skeletons = [];
		for (var i = 0; i < count; i++) {
			Skeletons.push(<Grid item columns={4} key={i}>
				<Skeleton height={height} variant="rectangular"/>
			</Grid>);
		}
		return <div ref={this.ref} className="mdc-layout-grid__cell mdc-layout-grid__cell--span-12 library-browser-content">
			<Grid container item>{Skeletons}</Grid>
		</div>
	}

	private renderBreadcrumbs = () =>
		<div className="breadcrumbs">
			<span className="breadcrumb" onClick={() => { this.onSelectCategory(0);this.onSelectTopLevelCategory(0); }}><T>Categories</T></span>
			{ this.state.selectedTopLevelCategoryId > 0 ? ( 
				<> &gt; <span className="breadcrumb" onClick={() => { this.onSelectCategory(0, this.state.selectedTopLevelCategoryId);}}>{this.props.categories.getIn([this.state.selectedTopLevelCategoryId,'title'])}</span></>
			) : "" }
			{ this.state.selectedCategoryId > 0 ? ( 
				<> &gt; <span className="breadcrumb" onClick={() => { this.onSelectCategory(this.state.selectedCategoryId);}} >{this.props.categories.getIn([this.state.selectedTopLevelCategoryId,'categories',this.state.selectedCategoryId,'title'])}</span></>
			) : "" }
		</div>

	render = () => {
		let patterns = this.props.patterns.filter((value: IPatternStore, key: string) => this.isVisible(value.get('name')));

		// Parent should render a loader already
		if(this.props.loading) return <>
			{this.renderBreadcrumbs()}
			{this.renderSkeletons(PatternsPerPage - patterns.count())}
		</>

		// If we have patterns or haven't selected a category yet
		else if(patterns.count() || !this.state.selectedCategoryId) {
			return <>
				{this.renderBreadcrumbs()}
				<div ref={this.ref} className="mdc-layout-grid__cell mdc-layout-grid__cell--span-12 library-browser-content">
					{ this.state.selectedCategoryId > 0 || this.props.search != "" ? ( 
							<Grid container spacing={2}>{this.renderPatterns(patterns)}</Grid>
						) : (
							<Grid container spacing={2}>{this.getCategories()}</Grid>
						)
					}
				</div>
			</>
		}

		// Nothing to display then
		else {
			return <>
				{this.renderBreadcrumbs()}
				<span className="library-browser-noresults">No Results</span>
			</>
		}
	}
}